import { useState, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Message, Sort, Download } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import LayoutControl from "../common/LayoutControl";
import ChartCard from "../../../common/components/ChartCard";
import AgeGenderDistributionChart from "./charts/AgeGenderDistributionChart";
import PositionDistributionChart from "./charts/PositionDistributionChart";
import TurnoverReasonsChart from "./charts/TurnoverReasonsChart";
import TeacherTurnOverByAcademicYearChart from "./charts/TeacherTurnOverByAcademicYearChart";
import TeacherTurnOverByDistrictChart from "./charts/TeacherTurnOverByDistrictChart";
import TeacherDistrictDistributionChart from "./charts/TeacherDistrictDistributionChart";
import TeachingLevelDistributionChart from "./charts/TeachingLevelDistributionChart";
import TeacherTurnOverByPositionChart from "./charts/TeacherTurnOverByPositionChart";
import OversightSectionsSkeleton from "../../../common/components/OversightSectionsSkeleton";
import AcademicYearDistributionChart from "./charts/AcademicYearDistributionChart";
import StaffDistributionTable from "./charts/StaffDistributionTable";
import NotifyTeachersModal from "../common/NotifyTeachersModal";
import { useRequiredAuth } from "../../../common/guard/requiredAuth";

const PlacementOversight = (props) => {
  const { user, loading } = props;
  const { hasPermissions } = useRequiredAuth();

  const [
    isLoadingDemographicsAndWorkforce,
    setIsLoadingDemographicsAndWorkforce,
  ] = useState(true);
  const [
    isLoadingAcademicAndTeachingLevels,
    setIsLoadingAcademicAndTeachingLevels,
  ] = useState(true);
  const [isLoadingRemainingDistributions, setIsLoadingRemainingDistributions] =
    useState(true);

  const [demographicsAndWorkforceData, setDemographicsAndWorkforceData] =
    useState({});
  const [academicAndTeachingLevelsData, setAcademicAndTeachingLevelsData] =
    useState({});
  const [remainingDistributionsData, setRemainingDistributionsData] = useState(
    {}
  );

  const [layout, setLayout] = useState("default");
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [teachersAbove65, setTeachersAbove65] = useState([]);
  const [isLoadingTeachersAbove65, setIsLoadingTeachersAbove65] =
    useState(false);
  const [exportLoading, setExportLoading] = useState({ image: false, pdf: false });
  const isNotGlobal = user?.selectedEntity?.id !== "00000000";

  const [staffDistributionSortBy, setStaffDistributionSortBy] =
    useState("district");
  const staffDistributionTableRef = useRef(null);
  const contentRef = useRef(null);

  const handleStaffDistributionExport = () => {
    if (
      staffDistributionTableRef.current &&
      staffDistributionTableRef.current.exportToExcel
    ) {
      staffDistributionTableRef.current.exportToExcel();
    }
  };

  const handleExportImage = async () => {
    try {
      setExportLoading(prev => ({ ...prev, image: true }));
      const scale = 2;
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        quality: 1.0,
        height: contentRef.current.scrollHeight,
        width: contentRef.current.scrollWidth,
      });

      const link = document.createElement("a");
      link.download = `Placements-Overview-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
    } catch (error) {
      toast.error("Failed to export image. Please try again.");
    } finally {
      setExportLoading(prev => ({ ...prev, image: false }));
    }
  };

  const handleExportPDF = async () => {
    try {
      setExportLoading(prev => ({ ...prev, pdf: true }));
      const scale = 2;
      const canvas = await html2canvas(contentRef.current, {
        scale: scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: 0,
        quality: 1.0,
        height: contentRef.current.scrollHeight,
        width: contentRef.current.scrollWidth,
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? "l" : "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = (pdfHeight - imgHeight * ratio) / 2;

      pdf.addImage(
        imgData,
        "PNG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio,
        "",
        "FAST"
      );
      pdf.save(`Placements-Overview-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      toast.error("Failed to export PDF. Please try again.");
    } finally {
      setExportLoading(prev => ({ ...prev, pdf: false }));
    }
  };

  useEffect(() => {
    if (user?.selectedEntity) {
      fetchDemographicsAndWorkforceData();
    }
  }, [user?.selectedEntity]);

  const fetchDemographicsAndWorkforceData = async () => {
    try {
      setIsLoadingDemographicsAndWorkforce(true);
      const { data } = await axios.get(
        `/api/oversight/staff-placements/demographics-and-workforce`
      );
      setDemographicsAndWorkforceData(data);
    } catch (error) {
      toast.error("Failed to load demographics and workforce data");
    } finally {
      setIsLoadingDemographicsAndWorkforce(false);
      fetchAcademicAndTeachingLevelsData();
    }
  };

  const fetchAcademicAndTeachingLevelsData = async () => {
    try {
      setIsLoadingAcademicAndTeachingLevels(true);
      const { data } = await axios.get(
        `/api/oversight/staff-placements/academic-and-teaching-levels`
      );
      setAcademicAndTeachingLevelsData(data);
    } catch (error) {
      toast.error("Failed to load academic and teaching levels data");
    } finally {
      setIsLoadingAcademicAndTeachingLevels(false);
      fetchRemainingDistributionsData();
    }
  };

  const fetchRemainingDistributionsData = async () => {
    try {
      setIsLoadingRemainingDistributions(true);
      const { data } = await axios.get(
        `/api/oversight/staff-placements/remaining-distributions`
      );
      setRemainingDistributionsData(data);
    } catch (error) {
      toast.error("Failed to load remaining distributions data");
    } finally {
      setIsLoadingRemainingDistributions(false);
    }
  };

  const fetchTeachersAbove65 = async () => {
    try {
      setIsLoadingTeachersAbove65(true);
      const { data } = await axios.get(
        `/api/oversight/staff-placements/teachers-above-65`
      );
      setTeachersAbove65(data.teachersAbove65 || []);
    } catch (error) {
      toast.error("Failed to load teachers above 65");
      setTeachersAbove65([]);
    } finally {
      setIsLoadingTeachersAbove65(false);
    }
  };

  const handleNotifyTeachers = async ({ notifications }) => {
    try {
      setIsSending(true);
      await axios.post("/api/oversight/notify-teachers", { notifications });
      toast.success(
        `Successfully sent ${
          notifications.length === 1
            ? "notification to 1 teacher"
            : `notifications to ${notifications.length} teachers`
        }`
      );
      setIsNotifyModalOpen(false);
    } catch (error) {
      toast.error("Failed to send notifications. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleOpenNotifyModal = async () => {
    await fetchTeachersAbove65();
    setIsNotifyModalOpen(true);
  };

  const staffDistributionCustomActions = useMemo(() => {
    return [
      {
        label: "Sort by",
        icon: <Sort />,
        submenu: [
          {
            label: "District (A-Z)",
            onClick: () => setStaffDistributionSortBy("district"),
            selected: staffDistributionSortBy === "district",
          },
          {
            label: "Total Staff (High-Low)",
            onClick: () => setStaffDistributionSortBy("total"),
            selected: staffDistributionSortBy === "total",
          },
        ],
      },
      {
        label: "Export to Excel",
        icon: <Download />,
        onClick: handleStaffDistributionExport,
      },
    ];
  }, [staffDistributionSortBy]);

  const getLayoutClass = (index) => {
    switch (layout) {
      case "leftWide":
        return index % 2 === 0 ? "col-12 col-md-8 p-0" : "col-12 col-md-4 p-0";
      case "rightWide":
        return index % 2 === 0 ? "col-12 col-md-4 p-0" : "col-12 col-md-8 p-0";
      case "full":
        return "col-12 p-0";
      default:
        return "col-12 col-md-6 p-0";
    }
  };

  if (loading) {
    return <OversightSectionsSkeleton />;
  }

  return (
    <div className="pl-4 pt-2">
      <LayoutControl 
        layout={layout} 
        setLayout={setLayout} 
        onExportImage={handleExportImage}
        onExportPDF={handleExportPDF}
        exportLoading={exportLoading}
      />
      <NotifyTeachersModal
        open={isNotifyModalOpen}
        onClose={() => setIsNotifyModalOpen(false)}
        teachers={teachersAbove65}
        onSendNotification={handleNotifyTeachers}
        isLoading={isSending || isLoadingTeachersAbove65}
      />
      <div className="row w-100" ref={contentRef}>
        {isLoadingDemographicsAndWorkforce ? (
          <div className="col-12">
            <OversightSectionsSkeleton height={300} />
          </div>
        ) : (
          <>
            <div className={getLayoutClass(0)}>
              <ChartCard
                title={`Teacher Age Structure ${
                  isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
                }`}
                subheader="Age and gender distribution of teachers"
                customActions={
                  hasPermissions(["OVERSIGHT_NOTIFY_RETIREMENT"])
                    ? [
                        {
                          label: isLoadingTeachersAbove65
                            ? "Loading..."
                            : "Notify 65 & up",
                          onClick: handleOpenNotifyModal,
                          icon: isLoadingTeachersAbove65 ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <Message />
                          ),
                          loading: isLoadingTeachersAbove65,
                        },
                      ]
                    : []
                }
              >
                <AgeGenderDistributionChart
                  data={demographicsAndWorkforceData?.ageGenderDistributions}
                  summary={
                    demographicsAndWorkforceData?.ageGenderDistributionSummary
                  }
                />
              </ChartCard>
            </div>
            <div className={getLayoutClass(1)}>
              <ChartCard
                title={`Distribution of Teachers ${
                  isNotGlobal
                    ? `In ${user?.selectedEntity?.name}`
                    : "across Districts"
                }`}
                subheader="Number of teachers and workforce by by entity"
              >
                <TeacherDistrictDistributionChart
                  data={
                    demographicsAndWorkforceData?.teachersDistrictDistributions
                  }
                  summary={
                    demographicsAndWorkforceData?.teachingWorkforceDistributionSummary
                  }
                />
              </ChartCard>
            </div>
          </>
        )}

        {isLoadingAcademicAndTeachingLevels ? (
          <div className="col-12">
            <OversightSectionsSkeleton height={300} />
          </div>
        ) : (
          <>
            <div
              className={
                user?.selectedScope?.id !== "RTB"
                  ? getLayoutClass(2)
                  : "col-12 p-0"
              }
            >
              <ChartCard
                title={`Academic Year Distribution ${
                  isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
                }`}
                subheader="Distribution of teachers by academic year"
              >
                <AcademicYearDistributionChart
                  data={
                    academicAndTeachingLevelsData?.academicYearDistributions
                  }
                />
              </ChartCard>
            </div>
            {user?.selectedScope?.id !== "RTB" && (
              <div className={getLayoutClass(3)}>
                <ChartCard
                  title={`Teaching Level Distribution ${
                    isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
                  }`}
                  subheader="Distribution of teachers by teaching level"
                >
                  <TeachingLevelDistributionChart
                    data={
                      academicAndTeachingLevelsData?.teachingLevelDistributions
                    }
                  />
                </ChartCard>
              </div>
            )}
            <div className="col-12 p-0">
              <ChartCard
                title={`Staff Distribution by Teaching Level, Role and Qualification ${
                  isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
                }`}
                subheader="Detailed breakdown of staff by teaching level, classification, and qualification"
                customActions={staffDistributionCustomActions}
                hideDefaultActions={true}
              >
                <StaffDistributionTable
                  data={
                    academicAndTeachingLevelsData?.staffByLevelRoleAndQualification
                  }
                  sortBy={staffDistributionSortBy}
                  ref={staffDistributionTableRef}
                />
              </ChartCard>
            </div>
          </>
        )}

        {isLoadingRemainingDistributions ? (
          <div className="col-12">
            <OversightSectionsSkeleton height={300} />
          </div>
        ) : (
          <>
            <div className={getLayoutClass(4)}>
              <ChartCard
                title={`Teacher Turnover by Academic Year ${
                  isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
                }`}
                subheader="Teacher turnovers by academic year due to various reasons"
              >
                <TeacherTurnOverByAcademicYearChart
                  data={
                    remainingDistributionsData?.turnOverByAcademicYearDistributions
                  }
                />
              </ChartCard>
            </div>

            <div className={getLayoutClass(5)}>
              <ChartCard
                title={`Turnover Reasons ${
                  isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
                }`}
                subheader="Reasons for teacher turnovers by teaching level"
              >
                <TurnoverReasonsChart
                  data={
                    remainingDistributionsData?.turnoverReasonsDistributions
                  }
                  scope={user?.selectedScope?.id}
                />
              </ChartCard>
            </div>

            <div className={getLayoutClass(6)}>
              <ChartCard
                title={`Teacher Turnover by District ${
                  isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
                }`}
                subheader="Number of teacher turnovers by district"
              >
                <TeacherTurnOverByDistrictChart
                  data={
                    remainingDistributionsData?.turnOverByDistrictDistributions
                  }
                />
              </ChartCard>
            </div>

            <div className={getLayoutClass(7)}>
              <ChartCard
                title={`Teacher Turnover by Position ${
                  isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
                }`}
                subheader="Number of teacher turnovers by position"
              >
                <TeacherTurnOverByPositionChart
                  data={
                    remainingDistributionsData?.turnoverByPositionDistribution
                  }
                />
              </ChartCard>
            </div>

            <div className="col-12 p-0">
              <ChartCard
                title={`Position Distribution ${
                  isNotGlobal ? `In ${user?.selectedEntity?.name}` : ""
                }`}
                subheader="Distribution of staff by position"
                hideActions={true}
              >
                <PositionDistributionChart
                  data={remainingDistributionsData?.positionDistributions}
                  scope={user?.selectedScope?.id}
                />
              </ChartCard>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = ({ user, loading }) => {
  return { user, loading };
};

export default connect(mapStateToProps, {})(PlacementOversight);
